import {Form as InertiaForm} from '@inertiajs/react'
import { type ChangeEvent, type ComponentProps, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Switch } from '@/components/ui/switch.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import type { CategoryType, CommunityOptionType, TaxModuleOptionType, TaxModulesByCommunityType } from './types.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  category: CategoryType
  tax_modules?: TaxModuleOptionType[]
  communities?: CommunityOptionType[]
  can_select_community?: boolean
  tax_modules_by_community?: TaxModulesByCommunityType
  submitText: string
}

export default function Form({
  category,
  tax_modules = [],
  communities = [],
  can_select_community = false,
  tax_modules_by_community,
  submitText,
  ...formProps
}: FormProps) {
  const canSelectCommunity = can_select_community && communities.length > 0
  const [selectedCommunityId, setSelectedCommunityId] = useState(category.community_id || communities[0]?.id || "")
  const [selectedTaxModuleId, setSelectedTaxModuleId] = useState(category.tax_module_id || "")
  const [active, setActive] = useState(Boolean(category.active))
  const filteredTaxModules = useMemo(() => {
    if (!canSelectCommunity || !tax_modules_by_community) {
      return tax_modules
    }

    return tax_modules_by_community[selectedCommunityId] || []
  }, [canSelectCommunity, selectedCommunityId, tax_modules, tax_modules_by_community])
  const hasTaxModules = filteredTaxModules.length > 0

  const handleCommunityChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCommunityId(event.target.value)
    setSelectedTaxModuleId("")
  }

  return (
    <InertiaForm
      transform={data => ({ category: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Informations de la catégorie</CardTitle>
            <CardDescription>Renseignez les détails de la catégorie et associez un module fiscal.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                type="text"
                name="name"
                id="name"
                defaultValue={category.name}
                placeholder="Nom de la catégorie"
              />
              {errors.name && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.name) ? errors.name.join(', ') : errors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                name="description"
                id="description"
                defaultValue={category.description}
                rows={4}
                placeholder="Description courte..."
              />
              {errors.description && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.description) ? errors.description.join(', ') : errors.description}
                </p>
              )}
            </div>

            {canSelectCommunity && (
              <div className="space-y-2">
                <Label htmlFor="community_id">Commune</Label>
                <select
                  name="community_id"
                  id="community_id"
                  value={selectedCommunityId}
                  onChange={handleCommunityChange}
                  className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="" disabled>Sélectionner une commune</option>
                  {communities.map((community) => (
                    <option key={community.id} value={community.id}>
                      {community.name} ({community.code})
                    </option>
                  ))}
                </select>
                {errors.community_id && (
                  <p className="text-sm font-medium text-destructive">
                    {Array.isArray(errors.community_id) ? errors.community_id.join(', ') : errors.community_id}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tax_module_id">Module fiscal</Label>
              <select
                name="tax_module_id"
                id="tax_module_id"
                value={selectedTaxModuleId}
                onChange={event => setSelectedTaxModuleId(event.target.value)}
                className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                required
                disabled={!hasTaxModules}
              >
                <option value="" disabled>
                  {hasTaxModules ? "Sélectionner un module fiscal" : "Aucun module fiscal disponible"}
                </option>
                {filteredTaxModules.map((taxModule) => (
                  <option key={taxModule.id} value={taxModule.id}>
                    {taxModule.name} ({taxModule.code})
                  </option>
                ))}
              </select>
              {!hasTaxModules && (
                <p className="text-sm font-medium text-amber-600">
                  Aucun module fiscal trouvé pour votre communauté. Créez d'abord un module fiscal.
                </p>
              )}
              {errors.tax_module_id && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.tax_module_id) ? errors.tax_module_id.join(', ') : errors.tax_module_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="active">Actif</Label>
              <input type="hidden" name="active" value={active ? '1' : '0'} />
              <div className="flex items-center gap-3">
                <Switch
                  id="active"
                  checked={active}
                  onCheckedChange={setActive}
                  aria-label="Basculer le statut actif"
                />
                <span className="text-sm text-muted-foreground">
                  {active ? 'Activé' : 'Désactivé'}
                </span>
              </div>
              {errors.active && (
                <p className="text-sm font-medium text-destructive">
                  {Array.isArray(errors.active) ? errors.active.join(', ') : errors.active}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-4">

              <Button type="submit" disabled={processing || !hasTaxModules}>
                {submitText}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </InertiaForm>
  )
}
